import { addDays } from './dates';
import { blackoutOn } from './schedule';
import { cancelStaleSwaps } from './swaps';
import type { Attendance, Blackout, Member, ScheduleEvent, ScheduleResponse } from './types';

/**
 * Confirmação de presença e check-in do dia.
 *
 * - **Resposta** (`event.responses`): o membro escalado numa escala publicada confirma
 *   ou avisa que não pode ir. Sem resposta, está pendente. A recusa tira a pessoa da
 *   função e deixa a vaga reaberta, com o motivo, para o líder preencher.
 * - **Check-in** (`event.attendance`): no dia (ou depois), o líder marca quem compareceu,
 *   quem faltou e quem serviu no lugar. É o que alimenta o histórico real e a frequência.
 */

export type PresenceState = 'confirmed' | 'declined' | 'pending';

export const PRESENCE_LABEL: Record<PresenceState, string> = {
  confirmed: 'Confirmado',
  declined: 'Não pode ir',
  pending: 'Aguardando confirmação',
};

/** Motivo padrão das datas marcadas em "Datas em que não posso". */
export const QUICK_BLACKOUT_REASON = 'Não posso';

/** Ids de quem está escalado, sem repetição. */
function scheduled(event: ScheduleEvent, roleIds: readonly string[] | null = null): string[] {
  return [
    ...new Set(
      Object.entries(event.slots)
        .filter(([roleId]) => !roleIds || roleIds.includes(roleId))
        .flatMap(([, ids]) => ids)
    ),
  ];
}

/** Situação da resposta de quem está (ou estava) escalado. */
export function presenceOf(event: ScheduleEvent, memberId: string): PresenceState {
  return event.responses[memberId]?.status ?? 'pending';
}

export interface PresenceSummary {
  confirmed: number;
  pending: number;
  declined: number;
  /** Quem ainda não respondeu (só os escalados). */
  pendingIds: string[];
}

/** Confirmações do evento, recortadas pelas funções (`null` = todas). */
export function presenceSummary(
  event: ScheduleEvent,
  roleIds: readonly string[] | null = null
): PresenceSummary {
  const ids = scheduled(event, roleIds);
  const pendingIds = ids.filter((id) => !event.responses[id]);

  return {
    confirmed: ids.filter((id) => event.responses[id]?.status === 'confirmed').length,
    pending: pendingIds.length,
    declined: declines(event, roleIds).length,
    pendingIds,
  };
}

export interface Decline extends ScheduleResponse {
  memberId: string;
}

/** Recusas cuja vaga ainda não voltou a ter a pessoa (a "Vaga reaberta" do painel). */
export function declines(event: ScheduleEvent, roleIds: readonly string[] | null = null): Decline[] {
  return Object.entries(event.responses)
    .filter(([, r]) => r.status === 'declined' && (!roleIds || roleIds.includes(r.roleId)))
    .map(([memberId, r]) => ({ memberId, ...r }))
    .sort((a, b) => b.at - a.at);
}

/** A pessoa pode responder: escala publicada, ainda não aconteceu e ela está escalada. */
export function canRespond(event: ScheduleEvent, memberId: string, today: string): boolean {
  return event.published && event.date >= today && scheduled(event).includes(memberId);
}

/**
 * Desfazer a resposta (o "Desfazer" do aviso, logo depois de responder): a escala segue
 * publicada e futura, e há resposta. Desfazer "Não posso ir" exige que a recusa ainda
 * esteja lá — se alguém já preencheu a vaga, a pessoa não volta.
 */
export function canRetract(event: ScheduleEvent, memberId: string, today: string): boolean {
  const response = event.responses[memberId];

  if (!event.published || event.date < today || !response) return false;

  return response.status === 'confirmed' || !scheduled(event).includes(memberId);
}

/** O check-in vale no dia do evento e depois dele, para quem tem gente escalada. */
export function checkInOpen(event: ScheduleEvent, today: string): boolean {
  return event.date <= today && scheduled(event).length > 0;
}

export interface AttendanceSummary {
  present: number;
  absent: number;
  substituted: number;
  /** Escalados ainda sem marcação. */
  unmarked: number;
}

export function attendanceSummary(
  event: ScheduleEvent,
  roleIds: readonly string[] | null = null
): AttendanceSummary {
  const ids = scheduled(event, roleIds);
  const marks = ids.map((id) => event.attendance[id]);

  return {
    present: marks.filter((a) => a?.status === 'present').length,
    absent: marks.filter((a) => a?.status === 'absent').length,
    substituted: marks.filter((a) => a?.status === 'absent' && a.substituteId).length,
    unmarked: marks.filter((a) => !a).length,
  };
}

/** Rótulo do check-in de uma pessoa: "Presente", "Faltou" ou "Faltou · Ana no lugar". */
export function attendanceLabel(attendance: Attendance, substituteName = ''): string {
  if (attendance.status === 'present') return 'Presente';

  return attendance.substituteId && substituteName ? `Faltou · ${substituteName} no lugar` : 'Faltou';
}

export interface ServiceRecord {
  event: ScheduleEvent;
  roleId: string;
  /** `scheduled` (sem check-in), `present`, `absent` ou `substitute` (serviu no lugar de alguém). */
  kind: 'scheduled' | 'present' | 'absent' | 'substitute';
  /** No `substitute`: quem faltou. */
  replacedId: string | null;
}

/**
 * Participações do membro com o que o check-in registrou: escalado (sem marcação),
 * presente, faltou — e as vezes em que serviu no lugar de alguém, que contam como
 * participação mesmo sem estar na escala.
 */
export function serviceRecords(events: readonly ScheduleEvent[], memberId: string): ServiceRecord[] {
  return events.flatMap((event) => {
    const own = Object.entries(event.slots)
      .filter(([, ids]) => ids.includes(memberId))
      .map(([roleId]): ServiceRecord => {
        const mark = event.attendance[memberId];

        return { event, roleId, kind: mark?.status ?? 'scheduled', replacedId: null };
      });
    const covered = Object.entries(event.attendance)
      .filter(([, a]) => a.status === 'absent' && a.substituteId === memberId)
      .flatMap(([absentId]): ServiceRecord[] => {
        const roleId = Object.entries(event.slots).find(([, ids]) => ids.includes(absentId))?.[0];

        return roleId ? [{ event, roleId, kind: 'substitute', replacedId: absentId }] : [];
      });

    return [...own, ...covered];
  });
}

/**
 * Já aconteceu para o membro: evento de antes de hoje, ou de hoje com check-in feito
 * (presente, falta ou substituição) — o check-in do dia já vira histórico.
 */
export function isRealized(record: ServiceRecord, today: string): boolean {
  return record.event.date < today || (record.event.date === today && record.kind !== 'scheduled');
}

/** Ainda vai acontecer: escalado, sem check-in, de hoje em diante. */
export function isUpcoming(record: ServiceRecord, today: string): boolean {
  return record.kind === 'scheduled' && record.event.date >= today;
}

export interface Frequency {
  present: number;
  absent: number;
  substitute: number;
  /** Presença sobre as escalas com check-in (0–100), ou `null` sem check-in. */
  rate: number | null;
}

/** Frequência real a partir do check-in (só eventos até `today`). */
export function frequencyOf(records: readonly ServiceRecord[], today: string): Frequency {
  const past = records.filter((r) => r.event.date <= today);
  const present = past.filter((r) => r.kind === 'present').length;
  const absent = past.filter((r) => r.kind === 'absent').length;
  const substitute = past.filter((r) => r.kind === 'substitute').length;
  const marked = present + absent;

  return { present, absent, substitute, rate: marked ? Math.round((present / marked) * 100) : null };
}

/**
 * Mantém respostas, check-in e pedidos de troca coerentes com a escala: sai a marcação de
 * quem não está mais escalado; a recusa fica (é a "vaga reaberta") até a pessoa voltar à
 * escala; o pedido de troca que a escala tornou impossível é cancelado.
 */
export function syncTracking(input: ScheduleEvent): ScheduleEvent {
  const event = cancelStaleSwaps(input);
  const ids = new Set(scheduled(event));
  const responses = Object.fromEntries(
    Object.entries(event.responses).filter(([id, r]) =>
      r.status === 'declined' ? !ids.has(id) : ids.has(id)
    )
  );
  const attendance = Object.fromEntries(Object.entries(event.attendance).filter(([id]) => ids.has(id)));
  const same =
    Object.keys(responses).length === Object.keys(event.responses).length &&
    Object.keys(attendance).length === Object.keys(event.attendance).length;

  return same ? event : { ...event, responses, attendance };
}

/** Quantas semanas à frente "Datas em que não posso" mostra. */
export const QUICK_DATES_WEEKS = 6;

export interface QuickDate {
  date: string;
  /** Horários dos eventos do dia, em ordem. */
  times: string[];
  /** Marcada em "Datas em que não posso" (ausência de um dia só). */
  marked: boolean;
  /** Ausência mais longa (férias) que já cobre a data — só se desfaz pelo perfil. */
  blackout: Blackout | null;
  /** Escalado numa escala publicada do dia: o caminho é "Não posso ir" na escala. */
  scheduled: boolean;
}

/**
 * Os dias com evento nas próximas semanas, para o membro marcar "não posso" com um toque.
 * Entram os eventos dos ministérios de que participa (todos, se ainda não participa de
 * nenhum).
 */
export function quickDates(
  events: readonly ScheduleEvent[],
  member: Member,
  today: string,
  weeks: number = QUICK_DATES_WEEKS
): QuickDate[] {
  const last = addDays(today, weeks * 7);
  const ministryIds = Object.keys(member.roles);
  const byDate = new Map<string, ScheduleEvent[]>();

  for (const event of events) {
    if (event.date < today || event.date > last) continue;
    if (ministryIds.length > 0 && !event.ministryIds.some((id) => ministryIds.includes(id))) continue;

    byDate.set(event.date, [...(byDate.get(event.date) ?? []), event]);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, dayEvents]) => {
      const covering = blackoutOn(member, date) ?? null;
      const marked = member.blackouts.some((b) => b.from === date && b.to === date);

      return {
        date,
        times: [...new Set(dayEvents.map((e) => e.time))].sort(),
        marked,
        blackout: marked ? null : covering,
        scheduled: dayEvents.some((e) => e.published && scheduled(e).includes(member.id)),
      };
    });
}
